import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, CheckCircle, Mail, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { BootstrapPhase } from '@/features/app-state';
import {
  selectActiveInvitation,
  selectActiveInvitationErrorMessage,
  selectActiveInvitationErrorReason,
  selectActiveInvitationPhase,
  selectActiveInvitationToken,
  setActiveInvitationToken,
  verifyInvitationToken,
} from '@/features/invitations';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

export default function InvitePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isRestoringSession = useAppSelector((state) => state.auth.isRestoringSession);
  const bootstrapPhase = useAppSelector((state) => state.appState.bootstrapPhase);
  const activeToken = useAppSelector(selectActiveInvitationToken);
  const invitationData = useAppSelector(selectActiveInvitation);
  const invitationPhase = useAppSelector(selectActiveInvitationPhase);
  const errorReason = useAppSelector(selectActiveInvitationErrorReason);
  const errorMessage = useAppSelector(selectActiveInvitationErrorMessage);
  const effectiveToken = tokenFromQuery ?? activeToken;

  useEffect(() => {
    if (tokenFromQuery) {
      if (tokenFromQuery !== activeToken) {
        dispatch(setActiveInvitationToken(tokenFromQuery));
        void dispatch(verifyInvitationToken(tokenFromQuery));
        return;
      }

      if (invitationPhase === 'idle') {
        void dispatch(verifyInvitationToken(tokenFromQuery));
      }
      return;
    }

    if (activeToken && invitationPhase === 'idle') {
      void dispatch(verifyInvitationToken(activeToken));
    }
  }, [activeToken, dispatch, invitationPhase, tokenFromQuery]);

  useEffect(() => {
    if (!effectiveToken || !isAuthenticated || isRestoringSession || invitationPhase !== 'ready') return;
    if (bootstrapPhase === BootstrapPhase.Idle || bootstrapPhase === BootstrapPhase.Unauthenticated) return;

    navigate('/invite/accept', { replace: true });
  }, [bootstrapPhase, effectiveToken, invitationPhase, isAuthenticated, isRestoringSession, navigate]);

  const waitingForBootstrapRedirect =
    invitationPhase === 'ready' &&
    isAuthenticated &&
    (isRestoringSession || bootstrapPhase === BootstrapPhase.Idle || bootstrapPhase === BootstrapPhase.Unauthenticated);

  if (
    (effectiveToken && invitationPhase === 'idle') ||
    invitationPhase === 'verifying' ||
    waitingForBootstrapRedirect
  ) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <LoadingSpinner size={80} />
        <p className="text-muted-foreground">
          {waitingForBootstrapRedirect ? 'Preparing your session…' : 'Verifying invitation…'}
        </p>
      </div>
    );
  }

  if (!effectiveToken || invitationPhase === 'error' || !invitationData) {
    const errorConfig = {
      expired: {
        icon: <XCircle className="text-destructive h-12 w-12" />,
        title: 'Invitation Expired',
        description: 'This invitation link has expired. Please contact your administrator for a new invitation.',
        showLogin: false,
      },
      accepted: {
        icon: <CheckCircle className="h-12 w-12 text-green-500" />,
        title: 'Already Accepted',
        description: 'This invitation has already been accepted. You can sign in to your account.',
        showLogin: true,
      },
      revoked: {
        icon: <AlertCircle className="h-12 w-12 text-yellow-500" />,
        title: 'Invitation Cancelled',
        description: 'This invitation has been cancelled. Please contact your administrator.',
        showLogin: false,
      },
      not_found: {
        icon: <XCircle className="text-destructive h-12 w-12" />,
        title: 'Invalid Invitation',
        description: 'This invitation link is invalid. Please check the link and try again.',
        showLogin: false,
      },
    };

    const config = errorConfig[errorReason as keyof typeof errorConfig] || errorConfig.not_found;
    const description = errorMessage || config.description;

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center">{config.icon}</div>
            <CardTitle>{config.title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            {config.showLogin ? (
              <Button asChild className="w-full">
                <Link to="/login">Go to Login</Link>
              </Button>
            ) : (
              <Button variant="outline" asChild className="w-full">
                <Link to="/">Back to Home</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Join {invitationData?.orgName}</CardTitle>
          <CardDescription>
            {invitationData?.inviterName
              ? `You've been invited by ${invitationData.inviterName}`
              : "You've been invited to join this organization"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="bg-muted/50 flex items-center gap-3 rounded-lg p-3">
              <Mail className="text-muted-foreground h-5 w-5" />
              <div className="flex-1">
                <p className="text-muted-foreground text-xs">Invited email</p>
                <p className="text-sm font-medium">{invitationData?.email}</p>
              </div>
            </div>
            <div className="bg-muted/50 flex items-center gap-3 rounded-lg p-3">
              <Building2 className="text-muted-foreground h-5 w-5" />
              <div className="flex-1">
                <p className="text-muted-foreground text-xs">Organization</p>
                <p className="text-sm font-medium">{invitationData?.orgName}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Sign in with the invited account to review and accept this organization invitation.
            </p>
            <Button asChild className="w-full">
              <Link to="/login">Sign in to accept invitation</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
